/**
 * Workflows Page - Task Chains, Batch Processing, History
 * Phase 2 Core Workflows feature
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Workflow, Link2, Layers, History, Image, Video, 
  Plus, Play, Trash2, Clock, CheckCircle, XCircle,
  ArrowRight, Upload, Download, RefreshCw, Grid
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkflowsPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('chains');
  const [chains, setChains] = useState([]);
  const [batches, setBatches] = useState([]);
  const [history, setHistory] = useState([]);
  const [gallery, setGallery] = useState({ media: [], stats: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && token) {
      fetchData();
    }
  }, [user, token, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'chains') {
        const res = await axios.get(`${API}/workflows/chains`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setChains(res.data.chains || []);
      } else if (activeTab === 'batches') {
        const res = await axios.get(`${API}/workflows/batches`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBatches(res.data.batches || []);
      } else if (activeTab === 'history') {
        const res = await axios.get(`${API}/workflows/history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHistory(res.data.history || []);
      } else if (activeTab === 'gallery') {
        const res = await axios.get(`${API}/workflows/gallery`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setGallery(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const runChain = async (chainId) => {
    try {
      await axios.post(`${API}/workflows/chains/${chainId}/run`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Failed to run chain:', err);
    }
  };

  const deleteChain = async (chainId) => {
    try {
      await axios.delete(`${API}/workflows/chains/${chainId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Failed to delete chain:', err);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16 flex items-center justify-center">
        <div className="text-center">
          <Workflow className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Sign in to access Workflows</h2>
          <p className="text-zinc-400 mb-6">Create task chains, batch process, and view your history</p>
          <Button onClick={() => navigate('/login')} className="bg-teal-500 hover:bg-teal-600">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'chains', label: 'Task Chains', icon: Link2 },
    { id: 'batches', label: 'Batch Jobs', icon: Layers },
    { id: 'history', label: 'History', icon: History },
    { id: 'gallery', label: 'Gallery', icon: Grid }
  ];

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-white mb-2">Workflows</h1>
          <p className="text-zinc-400">Chain tasks, batch process, and manage your AI outputs</p>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
                    : 'bg-zinc-800/50 text-zinc-400 border border-zinc-700 hover:border-zinc-600'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Task Chains Tab */}
        {activeTab === 'chains' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-white">Your Task Chains</h2>
              <Button className="bg-teal-500 hover:bg-teal-600" onClick={() => navigate('/workflows/create-chain')}>
                <Plus className="w-4 h-4 mr-2" />
                Create Chain
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-12 text-zinc-500">Loading...</div>
            ) : chains.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <Link2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-white font-semibold mb-2">No Task Chains Yet</h3>
                <p className="text-zinc-400 mb-4">Link multiple templates together for automated workflows</p>
                <Button variant="outline" className="border-zinc-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Chain
                </Button>
              </div>
            ) : (
              <div className="grid gap-4">
                {chains.map((chain) => (
                  <div
                    key={chain.id}
                    className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 flex items-center justify-between"
                  >
                    <div>
                      <h3 className="text-white font-semibold">{chain.name}</h3>
                      <p className="text-zinc-500 text-sm">{chain.steps?.length || 0} steps • Run {chain.run_count || 0} times</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => runChain(chain.id)} className="bg-green-500/20 text-green-400 hover:bg-green-500/30">
                        <Play className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => deleteChain(chain.id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Batch Jobs Tab */}
        {activeTab === 'batches' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-white">Batch Processing Jobs</h2>
              <Button className="bg-purple-500 hover:bg-purple-600" onClick={() => navigate('/workflows/batch')}>
                <Upload className="w-4 h-4 mr-2" />
                New Batch
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-12 text-zinc-500">Loading...</div>
            ) : batches.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <Layers className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-white font-semibold mb-2">No Batch Jobs</h3>
                <p className="text-zinc-400 mb-4">Upload a CSV to process multiple items at once</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {batches.map((batch) => (
                  <div
                    key={batch.id}
                    className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-white font-semibold">{batch.template_id}</h3>
                        <p className="text-zinc-500 text-sm">
                          {batch.completed_items}/{batch.total_items} items • {batch.status}
                        </p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        batch.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        batch.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {batch.status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <h2 className="text-xl font-semibold text-white mb-6">Task History</h2>
            
            {loading ? (
              <div className="text-center py-12 text-zinc-500">Loading...</div>
            ) : history.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <History className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-white font-semibold mb-2">No History Yet</h3>
                <p className="text-zinc-400">Complete some tasks to see them here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((task) => (
                  <div
                    key={task.id}
                    className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex items-center justify-between hover:border-zinc-700 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        task.status === 'completed' ? 'bg-green-500/20' : 'bg-yellow-500/20'
                      }`}>
                        {task.status === 'completed' ? (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        ) : (
                          <Clock className="w-5 h-5 text-yellow-400" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-white font-medium">{task.template_name || task.template_id}</h3>
                        <p className="text-zinc-500 text-sm">{new Date(task.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="border-zinc-700">
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Rerun
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-semibold text-white">Output Gallery</h2>
                <p className="text-zinc-500 text-sm">
                  {gallery.stats?.total_images || 0} images • {gallery.stats?.total_videos || 0} videos
                </p>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12 text-zinc-500">Loading...</div>
            ) : gallery.media?.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <Image className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-white font-semibold mb-2">No Media Yet</h3>
                <p className="text-zinc-400 mb-4">Generate images or videos to see them here</p>
                <Button onClick={() => navigate('/templates')} className="bg-teal-500 hover:bg-teal-600">
                  Create Media
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {gallery.media.map((item) => (
                  <div
                    key={item.id}
                    className="relative group bg-zinc-900 rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-colors"
                  >
                    {item.thumbnail ? (
                      <img
                        src={`data:image/jpeg;base64,${item.thumbnail}`}
                        alt="Generated media"
                        className="w-full h-40 object-cover"
                      />
                    ) : (
                      <div className="w-full h-40 bg-zinc-800 flex items-center justify-center">
                        {item.type === 'video' ? (
                          <Video className="w-8 h-8 text-zinc-600" />
                        ) : (
                          <Image className="w-8 h-8 text-zinc-600" />
                        )}
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        item.type === 'video' ? 'bg-pink-500/80 text-white' : 'bg-blue-500/80 text-white'
                      }`}>
                        {item.type?.toUpperCase()}
                      </span>
                    </div>
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button size="sm" className="bg-white/20 text-white hover:bg-white/30">
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
